"use client";

import { HeroWide as HeroWideUI } from "@repo/ui-parkmobile/HeroWide";
import type { HeroWide } from "@/generated/sb";
import {
  imageStoryblokResolver,
  buttonResolver,
  linkResolver,
  titleResolver,
  textResolver,
  sectionTitleResolver,
} from "@/lib/storyblok/adaptors";
import { storyblokEditable } from "@storyblok/react/rsc";
import FormContainer from "@/components/FormContainer";
import EmbeddedForm from "@/components/EmbeddedForm";
import {
  getSectionName,
  injectButtonAnalytics,
  getContentDescription,
} from "@/utils/analytics";
import {
  trackFindParkingClick,
  trackFindParkingError,
} from "@/utils/trackingScripts";
import { LocationSearchAutocomplete } from "@/components/LocationSearchAutocomplete";
import { createParkingSearchUrl } from "@/utils/parkingSearch";

interface HeroWideProps {
  blok: HeroWide;
  pageType?: "default" | "location";
  section?: string;
}

export default function HeroWide({
  blok,
  pageType = "default",
  section,
}: HeroWideProps) {
  if (!blok) {
    return null;
  }

  const imageBlok = blok.backgroundImage?.[0];
  const imageAsset = imageStoryblokResolver(imageBlok);

  const rawSectionTitle = blok.typography
    ? sectionTitleResolver(blok.typography)
    : undefined;

  const isLocationPage = pageType === "location";
  const showLocationSearch = !!blok.showLocationSearch;

  const sectionTitle = rawSectionTitle
    ? {
        ...rawSectionTitle,
        // TODO: Apply text-wrap: balance for location pages
        typography: isLocationPage
          ? rawSectionTitle.typography?.map((item) =>
              item.type === "title" ? { ...item, textWrap: "balance" as const } : item
            )
          : rawSectionTitle.typography,
      }
    : undefined;

  const titleContent = (
    sectionTitle?.typography?.find((item) => item.type === "title") as
      | { content: string }
      | undefined
  )?.content;
  const tagText = sectionTitle?.tagText;
  const sectionName =
    (tagText && titleContent ? `${tagText} ${titleContent}` : titleContent) ||
    getSectionName(blok.typography, section) ||
    "Hero Wide";
  const contentDescription = getContentDescription(blok.typography);

  if (sectionTitle) {
    sectionTitle.buttons = injectButtonAnalytics(
      sectionTitle.buttons,
      sectionName,
      contentDescription,
      "heroWide",
    );
  }

  const cards =
    blok.cards
      ?.slice(0, 3)
      .map((card) => {
        if (card.component === "cardPlans") {
          const iconImage = card.icon?.[0];
          const iconAsset = imageStoryblokResolver(iconImage);
          const linkBlok = card.link?.[0];
          const linkProps = linkBlok ? linkResolver([linkBlok]) : null;

          return {
            type: "plans" as const,
            editableProps: storyblokEditable(card),
            props: {
              iconTitle: titleResolver(card.iconTitle),
              icon: iconAsset,
              title: titleResolver(card.title),
              subtitle: textResolver(card.subtitle),
              text: textResolver(card.text),
              link:
                linkProps && linkBlok
                  ? {
                      text: linkBlok.text || "",
                      href: String(linkProps.href || ""),
                      target: linkProps.target,
                    }
                  : null,
              backgroundColor: card.backgroundColor || "none",
              verticalPadding: card.verticalPadding || "default",
              horizontalPadding: card.horizontalPadding || "default",
              ...storyblokEditable(card),
            },
          };
        } else if (card.component === "cardFeatures") {
          const iconImage = card.icon?.[0];
          const iconAsset = imageStoryblokResolver(iconImage);

          const features = card.features?.map((feature) => {
            const featureIconImage = feature.icon?.[0];
            const featureIconAsset = imageStoryblokResolver(featureIconImage);
            return {
              icon: featureIconAsset,
              text: feature.text,
            };
          });

          const buttonBlok = card.button?.[0];
          const buttonProps = buttonBlok
            ? buttonResolver(buttonBlok) || undefined
            : undefined;

          return {
            type: "features" as const,
            editableProps: storyblokEditable(card),
            props: {
              icon: iconAsset,
              title: titleResolver(card.title) || undefined,
              text: textResolver(card.text) || undefined,
              features: features || [],
              button: buttonProps,
              ...storyblokEditable(card),
            },
          };
        } else if (card.component === "image") {
          const imageAsset = imageStoryblokResolver(card);

          if (!imageAsset?.src) return null;

          return {
            type: "image" as const,
            editableProps: storyblokEditable(card),
            props: {
              ...imageAsset,
              quality: card.quality ? parseInt(card.quality) : undefined,
              ...storyblokEditable(card),
            },
          };
        } else if (card.component === "formContainer") {
          return {
            type: "form" as const,
            editableProps: storyblokEditable(card),
            props: <FormContainer blok={card} sectionName={sectionName} />,
          };
        } else if (card.component === "embeddedForm") {
          return {
            type: "form" as const,
            editableProps: storyblokEditable(card),
            props: <EmbeddedForm blok={card} sectionName={sectionName} />,
          };
        }
        return null;
      })
      .filter((card): card is NonNullable<typeof card> => card !== null) || [];

  const handleLocationSearch = (data: {
    destination: string;
    startDate: Date | null;
    endDate: Date | null;
    filterApplied: boolean;
    filterType?: string[];
  }) => {
    const selectedDate = data.startDate || new Date();

    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = selectedDate.getDate();
    const startDateFormatted = `${year}-${month}-${String(day).padStart(2, "0")}`;

    // Format date range if end date is provided
    let dateSelected = startDateFormatted;
    if (data.endDate) {
      const endYear = data.endDate.getFullYear();
      const endMonth = String(data.endDate.getMonth() + 1).padStart(2, "0");
      const endDay = String(data.endDate.getDate()).padStart(2, "0");
      dateSelected = `${startDateFormatted} - ${endYear}-${endMonth}-${endDay}`;
    }

    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const dayOfWeek = dayNames[selectedDate.getDay()] || "";

    trackFindParkingClick({
      section: sectionName,
      config: "heroWide",
      ctaText: blok.locationSearchButtonLabel || "Find parking",
      locationInput: data.destination,
      dateSelected,
      day,
      dayOfWeek,
      filterApplied: false, // Temporarily disabled
      // filterApplied: data.filterApplied,
      // filterType: data.filterType,
      contentDescription,
    });
  };

  const handleLocationSearchError = (data: {
    locationInput: string;
    dateSelected: string;
    errorType: string;
    errorMessage: string;
  }) => {
    trackFindParkingError({
      section: sectionName,
      config: "heroWide",
      ctaText: blok.locationSearchButtonLabel || "Find parking",
      locationInput: data.locationInput,
      dateSelected: data.dateSelected,
      errorType: data.errorType,
      errorMessage: data.errorMessage,
      contentDescription,
    });
  };

  return (
    <HeroWideUI
      {...storyblokEditable(blok)}
      image={imageAsset}
      imageSpace={blok.imageSpace || "fill"}
      enableOverlay={blok.enableOverlay}
      backgroundOverlay={blok.backgroundOverlay || "brand-50"}
      backgroundOverlayColor={blok.backgroundOverlayColor || "primary"}
      imageVerticalAlignment={
        blok.imageVerticalAlignment === "top" ||
        blok.imageVerticalAlignment === "bottom"
          ? blok.imageVerticalAlignment
          : "middle"
      }
      imageHorizontalAlignment={
        blok.imageHorizontalAlignment === "left" ||
        blok.imageHorizontalAlignment === "right"
          ? blok.imageHorizontalAlignment
          : "middle"
      }
      typographyPosition={
        blok.typographyPosition === "top" ||
        blok.typographyPosition === "center" ||
        blok.typographyPosition === "bottom"
          ? blok.typographyPosition
          : "center"
      }
      layout={blok.layout === "reversed" ? "reversed" : "default"}
      sectionTitle={sectionTitle || undefined}
      cards={cards.length > 0 ? cards : undefined}
      backgroundColor={blok.backgroundColor || "none"}
      topPadding={blok.topPadding || "none"}
      bottomPadding={blok.bottomPadding || "none"}
      variant={blok.variant === "fixed" ? "fixed" : "fit"}
      pageType={pageType}
      showLocationSearch={showLocationSearch}
      locationSearchProps={
        showLocationSearch
          ? {
              destinationPlaceholder:
                blok.locationSearchDestinationPlaceholder || undefined,
              datePlaceholder: blok.locationSearchDatePlaceholder || undefined,
              filterLabel: blok.locationSearchFilterLabel || undefined,
              searchButtonLabel: blok.locationSearchButtonLabel || undefined,
              onSearch: handleLocationSearch,
              onSearchError: handleLocationSearchError,
              renderDestinationInput: ({
                value,
                onChange,
                placeholder,
                error,
                onPlaceSelect,
              }) => (
                <LocationSearchAutocomplete
                  value={value}
                  onChange={onChange}
                  placeholder={placeholder}
                  error={error}
                  searchOptions={{ componentRestrictions: { country: "us" } }}
                  onPlaceSelect={() => onPlaceSelect()}
                />
              ),
              getSearchHref: (data) =>
                createParkingSearchUrl(data.destination, {
                  startDate: data.startDate,
                  endDate: data.endDate,
                  // TEMPORARILY DISABLED - Filter logic
                  // filterType: data.filterType,
                }),
            }
          : undefined
      }
      sectionFixedHeight={blok.sectionFixedHeight}
      id={blok.anchor}
    />
  );
}
